import { c as _c } from "react/compiler-runtime";
import { groq } from "next-sanity";
import { createContext, useCallback, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { getClient } from "../../../../../lib/sanity.client";
import { withStaticProps } from "../../../../../util/withStaticProps";
import { fetchCauseAreasAction } from "../store/layout/actions";
import { fetchReferralsAction } from "../store/referrals/actions";
import { setOperationsConfig, setCauseAreaSelection, setOperationsPercentageModeByCauseArea } from "../store/donation/actions";
import { State } from "../store/state";
import { WidgetProps } from "../types/WidgetProps";
import { Carousel } from "./Carousel";
import { SelectionPane } from "./panes/SelectionPane";
import { AmountPane } from "./panes/AmountPane/index";
import { SingleCauseAreaPane } from "./panes/SingleCauseAreaPane";
import { DonorPane } from "./panes/DonorPane/DonorPane";
import { PaymentPane } from "./panes/PaymentPane/PaymentPane";
import { ProgressBar } from "./shared/ProgressBar/ProgressBar";
import { ApiErrorNotification } from "./shared/ApiErrorNotification/ApiErrorNotification";
import { TaxInfoBox } from "./shared/Layout/Layout.style";
import { token } from "../../../../../token";
import { TooltipContent, TooltipLink, TooltipWrapper } from "./shared/ProgressBar/ProgressBar.style";
import { useAvailablePaymentMethods, useAvailableRecurringOptions, useDefaultPaymentMethodEffect, usePrefilledDistribution, usePrefilledSum, usePrefilledCauseAreaIds, useQueryParamsPrefill, useWidgetScaleEffect, WIDGET_CONTENT_WIDTH, WIDGET_FRAME_WIDTH } from "./hooks";
import { useElementHeight } from "../../../../../hooks/useElementHeight";
import { PrefilledDistribution } from "../../../../main/layout/WidgetPane/WidgetPane";
import { RecurringDonation } from "../types/Enums";
import { applyWidgetDefaults, DEFAULT_OPERATIONS_CONFIG } from "../utils/widgetDefaults";
export const widgetContentQuery = groq`
  ...,
  "locale": *[ _type == "site_settings"][0].main_locale,
  "accentColor": *[ _type == "site_settings"][0].accent_color,
  methods[] { 
    _type == 'reference' => @->{
      _type == 'bank' => {
        ...,
        transaction_cost,
        completed_redirect -> {
          "slug": slug.current,
        },
        "locale": *[ _type == "site_settings"][0].main_locale,
      },
      _type == 'vipps' => {
        _id,
        selector_text,
        recurring_title,
        recurring_selector_earliest_text,
        recurring_selector_choose_date_text,
        recurring_selector_date_picker_configuration->,
        recurring_button_text,
        single_title,
        single_button_text,
        transaction_cost,
      },
      _type == 'swish' => {
        ...,
        transaction_cost,
      },
      _type == 'autogiro' => {
        ...,
        transaction_cost,
        recurring_manual_option_config {
          ...,
          date_selector_config->
        }
      },
      _type == 'avtalegiro' => {
        ...,
        transaction_cost,
        date_selector_configuration->
      },
      _type == 'quickpay_card' => {
        ...,
        transaction_cost,
      },
      _type == 'quickpay_mobilepay' => {
        ...,
        transaction_cost,
      },
      _type == 'dkbank' => {
        ...,
        transaction_cost,
      },
    },
  },
  nudges[]{
    _key,
    message,
    minimum_amount,
    recurring_type,
    from_method->{
      _id,
      selector_text
    },
    to_method->{
      _id,
      selector_text
    },
  },
  privacy_policy_link {
    ...,
    "slug": page->slug.current,
    "pagetype": page->_type,
  },
`;
export const widgetQuery = groq`
*[_type == "donationwidget"][0] {
  ${widgetContentQuery}
}
`;
export const WidgetTooltipContext = createContext<[{
  text: string;
  link?: string;
} | null, any]>([null, () => {}]);
export const Widget = withStaticProps(async ({
  draftMode,
  inline,
  prefilled,
  defaultPaymentType
}: {
  draftMode: boolean;
  inline?: boolean;
  prefilled?: PrefilledDistribution;
  defaultPaymentType?: RecurringDonation;
}) => {
  const result = await getClient(draftMode ? token : undefined).fetch<WidgetProps>(widgetQuery);
  if (!result.methods?.length) {
    throw new Error("No payment methods found");
  }
  return {
    data: {
      result,
      query: widgetQuery
    },
    inline: inline ?? false,
    prefilled: prefilled ?? null,
    defaultPaymentType: defaultPaymentType ?? RecurringDonation.NON_RECURRING
  };
})(({
  data,
  inline = false,
  prefilled,
  defaultPaymentType
}) => {
  const widget = applyWidgetDefaults(data.result);
  const methods = widget.methods;
  if (!methods) {
    throw new Error("No payment methods found");
  }
  const dispatch = useDispatch<any>();
  const widgetRef = useRef<HTMLDivElement>(null);
  const widgetWrapperRef = useRef<HTMLDivElement>(null);
  const [tooltip, setTooltip] = useState<{
    text: string;
    link?: string;
  } | null>(null);
  const causeAreas = useSelector((state: State) => state.layout.causeAreas);
  const prefilledCauseAreaIds = usePrefilledCauseAreaIds();
  const availableRecurringOptions = useAvailableRecurringOptions(methods);
  const availablePaymentMethods = useAvailablePaymentMethods(methods);

  // Platforms with a single (active) cause area keep the pre-rewrite widget UX:
  // no cause-area selection step and no operations/tip. See SingleCauseAreaPane.
  const visibleCauseAreas = causeAreas?.filter(ca => ca.isActive || prefilledCauseAreaIds.has(ca.id)) ?? [];
  const isSingleCauseArea = !!causeAreas && visibleCauseAreas.length === 1;
  const singleCauseAreaId = isSingleCauseArea ? visibleCauseAreas[0].id : undefined;
  useEffect(() => {
    if (singleCauseAreaId === undefined) return;
    // There is nothing to select, so pin the selection to the one cause area and
    // make sure operations/tip stays off (it may be auto-enabled by config).
    dispatch(setCauseAreaSelection("single", singleCauseAreaId));
    dispatch(setOperationsPercentageModeByCauseArea(singleCauseAreaId, false));
  }, [dispatch, singleCauseAreaId]);
  const {
    scaledHeight,
    scalingFactor
  } = useWidgetScaleEffect(widgetRef, inline);
  // const { scrollPosition } = useWidgetScrollObserver(widgetRef);
  const widgetHeight = useElementHeight(widgetRef);
  useEffect(() => {
    // Set operations config first so it's available when cause areas are loaded
    dispatch(setOperationsConfig({
      operationsCauseAreaId: widget.operations_config?.operations_cause_area_id ?? DEFAULT_OPERATIONS_CONFIG.operations_cause_area_id,
      defaultPercentage: widget.operations_config?.default_percentage ?? DEFAULT_OPERATIONS_CONFIG.default_percentage,
      enabledByDefaultGlobal: widget.operations_config?.enabled_by_default_global ?? DEFAULT_OPERATIONS_CONFIG.enabled_by_default_global,
      enabledByDefaultSingle: widget.operations_config?.enabled_by_default_single ?? DEFAULT_OPERATIONS_CONFIG.enabled_by_default_single,
      excludedCauseAreaIds: widget.operations_config?.excluded_cause_area_ids ?? DEFAULT_OPERATIONS_CONFIG.excluded_cause_area_ids
    }));
    dispatch(fetchCauseAreasAction.started(undefined));
    dispatch(fetchReferralsAction.started(undefined));
  }, [dispatch, widget.locale]);
  usePrefilledDistribution({
    inline,
    causeAreas,
    prefilledDistribution: prefilled
  });
  usePrefilledSum({
    inline
  });
  useQueryParamsPrefill({
    inline,
    causeAreas,
    defaultPaymentType
  });
  useDefaultPaymentMethodEffect(methods);
  let tooltipReadmoreText: string;
  switch (widget.locale) {
    case "no":
      tooltipReadmoreText = "Les mer";
      break;
    case "sv":
      tooltipReadmoreText = "Läs mer";
      break;
    default:
      tooltipReadmoreText = "Read more";
  }
  return <div className="widget-wrapper" ref={widgetWrapperRef} style={{
    height: inline ? `${widgetHeight * scalingFactor}px` : "auto",
    width: scalingFactor * (inline ? WIDGET_CONTENT_WIDTH : WIDGET_FRAME_WIDTH)
  }}>
      <div className={`widget${inline ? " widget--inline" : ""}`} ref={widgetRef} style={{
      transform: `scale(${scalingFactor})`,
      height: inline ? "auto" : `${scaledHeight}px`,
      flexBasis: inline ? "auto" : `${scaledHeight}px`,
      transformOrigin: inline ? "top left" : undefined
    }}>
        <WidgetTooltipContext.Provider value={[tooltip, setTooltip]}>
          {tooltip !== null && <TooltipWrapper top={20 + 0}>
              <TooltipContent>{tooltip.text}</TooltipContent>
              {tooltip.link && <TooltipLink href={tooltip.link} target="_blank">
                  {tooltipReadmoreText} ↗
                </TooltipLink>}
            </TooltipWrapper>}
          <ApiErrorNotification genericErrorMessage={widget.api_generic_error_message} />
          {/*(() => {
            const taxInfoText: Record<string, string> = {
              no: "For skattefradrag i 2025 må pengene stå på vår konto innen årsslutt. Bankdonasjoner må overføres før kl. 14:30 den 31. desember.",
              sv: "För skatteavdrag 2025 måste pengarna finnas på vårt konto innan årsskiftet. Banköverföringar måste skickas före kl. 14:30 den 31 december.",
            };
            const text = taxInfoText[widget.locale];
            return text ? <TaxInfoBox>{text}</TaxInfoBox> : null;
           })()}*/}
          <ProgressBar inline={inline} numberOfPanes={isSingleCauseArea ? 3 : 4} />
          <Carousel minHeight={inline ? 0 : scaledHeight - 116}>
            {isSingleCauseArea ? <SingleCauseAreaPane nextButtonText={widget.pane1_button_text} enableRecurring={availableRecurringOptions.recurring} enableSingle={availableRecurringOptions.single} singleDonationText={widget.single_donation_text} monthlyDonationText={widget.monthly_donation_text} amountContext={widget.amount_context} smartDistributionContext={widget.smart_distribution_context} /> : false}
            {!isSingleCauseArea ? <SelectionPane causeAreaDisplayConfig={widget.cause_area_display_config} accentColor={widget.accentColor} /> : false}
            {!isSingleCauseArea ? <AmountPane nextButtonText={widget.pane1_button_text} smartDistContext={widget.smart_distribution_context} text={{
            single_donation_text: widget.single_donation_text,
            monthly_donation_text: widget.monthly_donation_text
          }} enableRecurring={availableRecurringOptions.recurring} enableSingle={availableRecurringOptions.single} amountContext={widget.amount_context} operationsConfig={widget.operations_config} causeAreaDisplayConfig={widget.cause_area_display_config} uiLabels={widget.ui_labels} /> : false}
            <DonorPane locale={widget.locale} text={{
            anon_button_text: widget.anon_button_text,
            anon_button_text_tooltip: widget.anon_button_text_tooltip,
            name_placeholder: widget.name_placeholder,
            name_invalid_error_text: widget.name_invalid_error_text,
            email_placeholder: widget.email_placeholder,
            email_invalid_error_text: widget.email_invalid_error_text,
            tax_deduction_selector_text: widget.tax_deduction_selector_text,
            tax_deduction_ssn_placeholder: widget.tax_deduction_ssn_placeholder,
            tax_deduction_ssn_invalid_error_text: widget.tax_deduction_ssn_invalid_error_text,
            tax_deduction_tooltip_text: widget.tax_deduction_tooltip_text,
            tax_deduction_ssn_suspicious_message: widget.tax_deduction_ssn_suspicious_message,
            newsletter_selector_text: widget.newsletter_selector_text,
            privacy_policy_text: widget.privacy_policy_text,
            privacy_policy_link: widget.privacy_policy_link,
            pane2_button_text: widget.pane2_button_text,
            api_generic_error_message: widget.api_generic_error_message,
            show_name_field: widget.show_name_field,
            allow_anonymous_donations: widget.allow_anonymous_donations,
            require_privacy_policy_checkbox: widget.require_privacy_policy_checkbox,
            privacy_policy_required_error_text: widget.privacy_policy_required_error_text,
            show_referral_code_field: widget.show_referral_code_field,
            referral_code_placeholder: widget.referral_code_placeholder
          }} summaryText={{
            single_donation_text: widget.single_donation_text,
            monthly_donation_text: widget.monthly_donation_text,
            smart_distribution_title: widget.smart_distribution_context.smart_distribution_title,
            operations_summary_label: widget.ui_labels?.operations_summary_label ?? "",
            total_label: widget.ui_labels?.total_label ?? ""
          }} paymentMethods={availablePaymentMethods} isSingleCauseArea={isSingleCauseArea} />
            <PaymentPane referrals={{
            referrals_title: widget.referrals_title,
            other_referral_input_placeholder: widget.other_referral_input_placeholder,
            show_referrals: widget.show_referrals
          }} paymentMethods={availablePaymentMethods} />
          </Carousel>
        </WidgetTooltipContext.Provider>
      </div>
    </div>;
});
export const useWidgetScrollObserver = elementRef => {
  const $ = _c(8);
  const [scrollPosition, setScrollPosition] = useState(0);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = new Set();
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const observers = useRef(t0);
  let t1;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = event => {
      const newPosition = event.target.scrollTop;
      setScrollPosition(newPosition);
      observers.current.forEach(observer => observer(newPosition));
    };
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const handleScroll = t1;
  let t2;
  let t3;
  if ($[2] !== elementRef) {
    t2 = () => {
      const element = elementRef.current;
      if (element) {
        element.addEventListener("scroll", handleScroll);
      }
      return () => {
        if (element) {
          element.removeEventListener("scroll", handleScroll);
        }
      };
    };
    t3 = [elementRef, handleScroll];
    $[2] = elementRef;
    $[3] = t2;
    $[4] = t3;
  } else {
    t2 = $[3];
    t3 = $[4];
  }
  useEffect(t2, t3);
  let t4;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = observer_0 => {
      observers.current.add(observer_0);
      return () => observers.current.delete(observer_0);
    };
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  const subscribe = t4;
  let t5;
  if ($[6] !== scrollPosition) {
    t5 = {
      scrollPosition,
      subscribe
    };
    $[6] = scrollPosition;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  return t5;
};